'use client';
import { Suspense, useCallback, useEffect, useRef } from 'react';
import { FlowContainer } from 'src/components/flow';
import * as screenComponents from 'src/components/screens';
import { Screen } from 'src/lib/flows/screens';
import { updateFormValues } from 'src/redux/actions/form';
import { getActiveFlow } from 'src/redux/actions/flow';
import { updateInput, UpdateInputPayload } from 'src/redux/reducers/inputs';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import {
  useCallbackEvent,
  useImpressionEvent,
  useInitializeGtm,
  useUserNavigatedEvent,
} from 'src/utils/analytics/hooks';
import { EventType, FormName } from 'src/utils/analytics/constants';
import {
  getInputs,
  getPageNumber,
  getInitialScreen,
  getIsSignedIn,
  getExperiments,
} from 'src/redux/selectors';
import { getFlowSectionName } from 'src/utils/analytics/utils';
import { AnalyticsPageView } from '@nerdwallet-cards/analytics';
import { isNerdyTipAndProgressBarActive } from 'src/lib/experiments';
import useSegmentRegistrationAttributes from '../utils/useSegmentRegistrationAttributes';
import QuizHeader from './quiz-header';
import { ScreenContainerProvider } from 'src/components/flow/screen-container/screen-container-provider';
import { SatellitePageViewRegistration } from '../components/SatellitePageViewRegistration';

type QuizBodyProps = {
  screenName: Screen;
};

const QuizBody: React.FC<QuizBodyProps> = ({ screenName }) => {
  const { navigationId } = useUserNavigatedEvent();

  const trackEvent = useCallbackEvent({
    navigationId,
  });

  const dispatch = useAppDispatch();
  const experiments = useAppSelector(getExperiments);
  const inputs = useAppSelector(getInputs);
  const pageNum = useAppSelector(getPageNumber);
  const initialScreen = useAppSelector(getInitialScreen);
  const isSignedIn = useAppSelector(getIsSignedIn);
  const sectionName = getFlowSectionName(screenName, inputs.primaryGoal);
  const formIsReady = useRef<boolean>(false);

  useInitializeGtm({
    isSignedIn,
    navigationId,
  });

  const { impressionId } = useImpressionEvent({
    eventType: EventType.PageImpression,
    immediate: true,
    navigationId,
    payload: {
      entity_name: sectionName,
      display_mode: 'standard',
    },
  });

  useSegmentRegistrationAttributes(
    {
      driverPageViewId: impressionId,
      driverPageName: sectionName,
    },
    [sectionName]
  );

  useEffect(() => {
    dispatch(updateFormValues(screenName));
    formIsReady.current = true;
  }, [dispatch, screenName]);

  const updateInputAction = useCallback(
    (payload: UpdateInputPayload) => dispatch(updateInput(payload)),
    [dispatch]
  );

  const ScreenComponent =
    screenComponents[screenName as keyof typeof screenComponents];

  const currentFlow = dispatch(getActiveFlow());

  const screen = currentFlow[screenName];

  const formName =
    screenName === Screen.WelcomeBack ? FormName.WelcomeBack : FormName.Flow;

  const { impressionId: formImpressionId } = useImpressionEvent({
    eventType: EventType.FormImpression,
    immediate: formIsReady.current,
    navigationId,
    payload: {
      entity_name: formName,
      form_name: formName,
      page_number: pageNum,
      parent_impression_id: impressionId,
      section_name: sectionName,
      section_position: 0,
      total_pages: null,
    },
  });

  return (
    <AnalyticsPageView name={sectionName}>
      <SatellitePageViewRegistration>
        <QuizHeader
          formImpressionId={formImpressionId}
          navigationId={navigationId}
        />
        <Suspense fallback={null}>
          <FlowContainer
            backgroundColor={
              screenName === Screen.WelcomeBack ? 'neutral.lightest' : undefined
            }
          >
            <ScreenContainerProvider>
              <ScreenComponent
                experiments={experiments}
                flow={currentFlow}
                isInitialScreen={screenName === initialScreen}
                inputs={inputs}
                updateInput={updateInputAction}
                pageNumber={pageNum}
                sectionName={sectionName}
                trackEvent={trackEvent}
                formImpressionId={formImpressionId}
                navigationId={navigationId}
                nerdyTip={
                  isNerdyTipAndProgressBarActive(experiments)
                    ? screen?.nerdyTip
                    : ''
                }
              />
            </ScreenContainerProvider>
          </FlowContainer>
        </Suspense>
      </SatellitePageViewRegistration>
    </AnalyticsPageView>
  );
};

export default QuizBody;
